import { useCallback, useEffect, useRef } from 'react';
import styles from '@/components/editor/components/mention/mention.module.css';
import type { TMentionOption } from '@/types/common';

interface MentionDropdownProps {
	options: TMentionOption[];
	selectedIndex: number;
	onSelect: (option: TMentionOption) => void;
	onClose: () => void;
	position: { top: number; left: number };
}

const MentionDropdown = ({
	options,
	selectedIndex,
	onSelect,
	onClose,
	position
}: MentionDropdownProps) => {
	const dropdownRef = useRef<HTMLDivElement>(null);

	const handleKeyDown = useCallback(
		(event: KeyboardEvent) => {
			if (event.key === 'Escape') {
				onClose();
			}
		},
		[onClose]
	);

	useEffect(() => {
		document.addEventListener('keydown', handleKeyDown);
		return () => {
			document.removeEventListener('keydown', handleKeyDown);
		};
	}, [handleKeyDown]);

	if (options.length === 0) {
		return null;
	}

	return (
		<div
			className={styles.dropdown}
			ref={dropdownRef}
			style={{
				position: 'absolute',
				top: position.top,
				left: position.left,
				zIndex: 1000
			}}
		>
			{options.map((option, index) => (
				<button
					className={`${styles.dropdownItem} ${
						index === selectedIndex ? styles.selected : ''
					}`}
					key={option.id}
					onClick={() => onSelect(option)}
					onKeyDown={(e) => {
						if (e.key === 'Enter' || e.key === ' ') {
							e.preventDefault();
							onSelect(option);
						}
					}}
					style={
						{
							'--mention-color': option.color
						} as React.CSSProperties
					}
					type="button"
				>
					{option.value}
				</button>
			))}
		</div>
	);
};

export default MentionDropdown;
